import React from 'react';
import Slider from 'react-slick';
import { Package, Plus, Settings } from 'lucide-react';
import { Card } from './Card';
import ActionButton from './ActionButton';
import { sliderSetting } from '../bookingHelper';

const BADGE_TONES = {
    danger: 'tw-bg-red-100 tw-text-red-700',
    warning: 'tw-bg-amber-100 tw-text-amber-800',
    info: 'tw-bg-blue-100 tw-text-blue-700',
    success: 'tw-bg-green-100 tw-text-green-800',
};

// Status labels, same rules and wording as the old product cards.
const productBadges = pp => [
    pp.status === 2 && { tone: 'danger', label: 'canceled' },
    pp.status === 3 && { tone: 'success', label: 'Refunded' },
    pp.itemStatus === 2 && { tone: 'danger', label: 'canceled' },
    pp.itemStatus === 3 && { tone: 'warning', label: 'canceled & Refunded' },
    pp.itemStatus === 4 && { tone: 'info', label: 'Refunded' },
].filter(Boolean);

const isCanceled = pp => pp?.itemStatus === 3 || pp?.itemStatus === 2 || pp.status === 2 || pp?.paymentArr?.at(-1)?.paymentStatus === 'failed';
const isRefunded = pp => pp.status === 3 || pp?.itemStatus === 4;

const ProductTile = ({ product: pp, hasPaymentHistory, onShowInfo, onEdit, onPaymentHistory, onPayNow }) => {
    const badges = productBadges(pp);
    return (
        <li className={`tw-flex tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border ${isCanceled(pp) ? 'tw-border-red-400' : 'tw-border-neutral-200'} ${isRefunded(pp) ? 'tw-bg-neutral-100' : 'tw-bg-white'}`}>
            {pp?.image?.length
                ? <div className="tw-mb-6">
                    <Slider {...sliderSetting}>
                        {pp.image.map((img, i) => (
                            <img
                                key={`${img}-${i}`}
                                src={img}
                                alt=""
                                onClick={() => onShowInfo(pp)}
                                className="tw-h-48 tw-w-full tw-cursor-pointer tw-object-contain tw-bg-neutral-50 tw-p-2 hover:tw-opacity-80"
                            />
                        ))}
                    </Slider>
                </div>
                : null}

            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-2 tw-p-3">
                {badges.length
                    ? <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                        {badges.map(b => <span key={b.label + b.tone} className={`tw-rounded tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold ${BADGE_TONES[b.tone]}`}>{b.label}</span>)}
                    </div>
                    : null}
                <div>
                    <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-800" style={pp.textColor ? { color: pp.textColor } : undefined}>{pp.name}</h3>
                    {pp.purchaseType === 2 ? <span className="tw-text-xs tw-text-neutral-500">Purchased with Program.</span> : null}
                </div>
                <dl className="tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-3 tw-gap-y-0.5 tw-text-sm">
                    <dt className="tw-text-neutral-500">Item price</dt>
                    <dd className="tw-m-0 tw-text-neutral-800">${pp?.itemPrice?.toFixed(2)}</dd>
                    <dt className="tw-text-neutral-500">Quantity</dt>
                    <dd className="tw-m-0 tw-text-neutral-800">{pp?.quantity}</dd>
                    {pp?.camperName
                        ? <>
                            <dt className="tw-text-neutral-500">Camper</dt>
                            <dd className="tw-m-0 tw-text-neutral-800">{pp.camperName}</dd>
                        </>
                        : null}
                </dl>
                <div className="tw-mt-auto tw-flex tw-flex-wrap tw-justify-between tw-gap-2 tw-pt-1">
                    <ActionButton variant="primary" icon={Settings} onClick={() => onEdit(pp)}>Edit</ActionButton>
                    {pp.purchaseType === 1
                        ? (hasPaymentHistory(pp)
                            ? <ActionButton variant="dark" onClick={() => onPaymentHistory(pp)}>Payment History</ActionButton>
                            : <ActionButton variant="primary" onClick={() => onPayNow(pp)}>Pay Now</ActionButton>)
                        : null}
                </div>
            </div>
        </li>
    );
};

/**
 * Products bought for this booking. Every button reuses CamperDetail's existing handlers:
 * Edit → cancel/refund, Payment History / Pay Now (only for products bought separately,
 * purchaseType 1), image click → product info, Add Product → the camp's product picker.
 */
const ProductsCard = ({ products = [], onAdd, ...tileHandlers }) => (
    <Card
        id="products"
        title="Products"
        icon={Package}
        actions={<ActionButton variant="primary" icon={Plus} onClick={onAdd}>Add Product</ActionButton>}
    >
        {products.length
            ? <ul className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
                {products.map((pp, i) => <ProductTile key={pp.ItemId || i} product={pp} {...tileHandlers} />)}
            </ul>
            : <p className="tw-text-sm tw-italic tw-text-neutral-400">No products added yet.</p>}
    </Card>
);

export default ProductsCard;
